// The Queue (Phase 6.3 Part 1, agreed with Kristi Oct 29): the list for Up Next and Future reads.
// Tabs, quick filters, sorting, ranks and where a dragged book lands. No database calls here,
// so the rules can be tested on their own (tests/logic/queue.test.ts).

import { fmtNum } from '../ui/ListFoot';
import { daysBetween } from '../book/model';
import type { ShelfBook } from '../shelves/model';
import type { FilterFacts } from '../shelves/filters';

export type QueueTab = 'up_next' | 'future_reads' | 'revisit';
export type ListTab = 'up_next' | 'future_reads';

export interface TabDef {
  key: QueueTab;
  name: string;
  description: string;
}

/** The tabs (decisions doc › The Queue becomes the full list; Kristi, Oct 21, 29 and 31). */
export const TABS: TabDef[] = [
  { key: 'up_next', name: 'Up Next', description: 'View or change the sequence of books on your Up Next shelf.' },
  { key: 'future_reads', name: 'Future reads', description: 'Sort and tidy the books you’d like to read someday, and select ones to move to Up Next.' },
  { key: 'revisit', name: 'Revisit', description: 'Decide what to do with books you set aside, want to read again, or passed on.' },
];
// Recently added moved to My books (Kristi, Oct 31; src/mybooks/model.ts).

/** A tab remembered on this device that the Queue no longer has (Recently added) opens Up Next. */
export const queueTab = (remembered: unknown): QueueTab => (TABS.some((t) => t.key === remembered) ? (remembered as QueueTab) : 'up_next');

export const tabDef = (k: QueueTab) => TABS.find((t) => t.key === k)!;

// ---------- quick filters ----------

export type QuickKey = 'not_owned' | 'long_on_up_next' | 'not_scored' | 'two_years' | 'no_copy' | 'no_read_type';

export interface QuickDef {
  key: QuickKey;
  label: string;
  test: (b: QueueRow, today: string) => boolean;
  /** A short explainer, shown on hover (and a long press). */
  hint?: string;
}

/**
 * What "On your list" and "On Up Next" count from (Kristi, Oct 31: a brief explainer). On your list
 * is the day the book joined your list: for imported books the sheet's Tracking since date, else
 * the earliest acquired date of a copy, else Oct 7, 2026; for books added here, the day you added it.
 */
export const WAITING_HINT: Record<ListTab, string> = {
  up_next: 'Since the book arrived on Up Next.',
  future_reads: 'Since the book joined your list: from the sheet, its Tracking since date (or its earliest copy’s date); added here, the day you added it.',
};

/**
 * The quick filters above the list (Kristi, Oct 29: above the list, not inside it), per tab.
 * Several on at once must all match. They come from the board's summary tiles.
 */
export const QUICK: Record<ListTab, QuickDef[]> = {
  up_next: [
    { key: 'not_owned', label: 'Not owned yet', test: (b) => !b.owned },
    { key: 'long_on_up_next', label: 'On Up Next 6+ months', hint: 'Arrived on Up Next 6 months ago or more.', test: (b, today) => Boolean(b.onShelfSince && daysBetween(b.onShelfSince.slice(0, 10), today) >= 182) },
    { key: 'not_scored', label: 'Interest not set', test: (b) => b.interest === null },
  ],
  future_reads: [
    { key: 'not_scored', label: 'Interest not set', test: (b) => b.interest === null },
    { key: 'two_years', label: 'On your list 2+ years', hint: 'Joined your list 2 years ago or more. ', test: (b, today) => Boolean(b.addedAt && daysBetween(b.addedAt.slice(0, 10), today) >= 730) },
    // No copy you have now: none of your own and no library loan or hold.
    { key: 'no_copy', label: 'No copy yet', test: (b) => !b.owned && !b.formats.length && !b.library.length },
    { key: 'no_read_type', label: 'No read type', test: (b) => b.readType === null && !b.readTypeName },
  ],
};

// ---------- the rows ----------

/** A book as a Queue row: the shelf's book, plus what the filters already know about it. */
export interface QueueRow extends ShelfBook {
  /** Place in the shelf's own order (1 = top), whatever the sort or filters. */
  rank: number;
  /** Formats of copies you have now (your own and active library loans). */
  formats: string[];
  /** Library steps (FilterFacts.library). */
  library: string[];
}

export function rowsOf(books: ShelfBook[], facts: Map<string, FilterFacts>): QueueRow[] {
  return books.map((b, i) => {
    const f = facts.get(b.id);
    return { ...b, rank: i + 1, owned: f ? f.owned : b.owned, formats: f?.formats ?? [], library: f?.library ?? [] };
  });
}

// ---------- sorting ----------

export type SortKey = 'rank' | 'title' | 'readtype' | 'interest' | 'goodreads' | 'waiting' | 'added' | 'copy';
export interface Sort {
  key: SortKey;
  /** Ascending as each sort's first click means it: Your order top first, Interest highest first… */
  reversed: boolean;
}

/** The sorts each tab offers (board 6), in the Sort menu's order. */
export const SORTS: Record<ListTab, [SortKey, string][]> = {
  up_next: [
    ['rank', 'Reading order'],
    ['interest', 'Interest'],
    ['goodreads', 'Goodreads'],
    ['waiting', 'Longest waiting'],
    ['readtype', 'Read type'],
    ['copy', 'Copy'],
    ['title', 'Title'],
  ],
  future_reads: [
    ['rank', 'Reading order'],
    ['interest', 'Interest'],
    ['goodreads', 'Goodreads'],
    ['waiting', 'Longest waiting'],
    ['added', 'Recently added'],
    ['readtype', 'Read type'],
    ['copy', 'Copy'],
    ['title', 'Title'],
  ],
};

/** A title as it files: without a leading The, A or An (as Reference's A–Z). */
export const titleKey = (t: string) => t.replace(/^(the|a|an)\s+/i, '').toLocaleLowerCase('en-US');

const TYPE_ORDER = ['light', 'absorbing', 'demanding', 'study'];

/** When a book started waiting: on Up Next, since it arrived there; in Future reads, since you added it. */
export const waitingSince = (b: Pick<ShelfBook, 'onShelfSince' | 'addedAt'>, tab: ListTab) => ((tab === 'up_next' ? b.onShelfSince : b.addedAt) ?? b.addedAt ?? '').slice(0, 10);

/**
 * The rows in a sort. Empty values (no Interest, no score, no read type) always go last,
 * whichever way the sort runs; ties keep your order.
 */
export function sortRows(rows: QueueRow[], sort: Sort, tab: ListTab): QueueRow[] {
  const dir = sort.reversed ? -1 : 1;
  type V = string | number | null;
  const val = (b: QueueRow): V => {
    switch (sort.key) {
      case 'rank':
        return b.rank;
      case 'title':
        return titleKey(b.title);
      case 'readtype': {
        const i = b.readType ? TYPE_ORDER.indexOf(b.readType) : -1;
        return i >= 0 ? i : b.readTypeName ? 4 : null;
      }
      case 'interest':
        return b.interest === null ? null : -b.interest; // highest first
      case 'goodreads':
        return b.goodreads === null ? null : -b.goodreads; // highest first
      case 'waiting':
        return waitingSince(b, tab) || null; // oldest first
      case 'added':
        return b.addedAt ? `~${invert(b.addedAt)}` : null; // newest first
      case 'copy':
        return copyText(b)?.toLocaleLowerCase('en-US') ?? null; // A–Z; Not owned yet last (Kristi, Oct 29)
    }
  };
  return [...rows].sort((a, b) => {
    const x = val(a);
    const y = val(b);
    if (x === null || y === null) return x === y ? a.rank - b.rank : x === null ? 1 : -1;
    const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return c ? c * dir : a.rank - b.rank;
  });
}

/** A date string turned so that newer sorts first as plain text. */
function invert(s: string): string {
  return [...s].map((ch) => (/\d/.test(ch) ? String(9 - Number(ch)) : ch)).join('');
}

/** "Your order" not reversed: the only sort where dragging and Move to # make sense (board 6). */
export const canReorder = (sort: Sort) => sort.key === 'rank' && !sort.reversed;

// ---------- dragging ----------

/**
 * Where a dragged book lands, from the rows showing (Kristi, Oct 29: with a filter on, next to the
 * book you dropped it beside; hidden books keep their places). Dropped on the top half of a row it
 * goes before that book, on the bottom half after it. Null when it wouldn't move.
 */
export function dropPlace(shown: string[], dragged: string, target: string, lowerHalf: boolean): { before: string } | { after: string } | null {
  if (dragged === target) return null;
  const from = shown.indexOf(dragged);
  const to = shown.indexOf(target);
  if (from < 0 || to < 0) return null;
  // Right above or below itself: no change.
  if (!lowerHalf && to === from + 1) return null;
  if (lowerHalf && to === from - 1) return null;
  return lowerHalf ? { after: target } : { before: target };
}

/** The order shown after a drop, for the screen while the database saves it. */
export function afterDrop(shown: string[], dragged: string, place: { before: string } | { after: string }): string[] {
  const rest = shown.filter((id) => id !== dragged);
  const other = 'before' in place ? place.before : place.after;
  const i = rest.indexOf(other);
  if (i < 0) return shown;
  rest.splice('before' in place ? i : i + 1, 0, dragged);
  return rest;
}

/**
 * Several books to a new place as a block (0041; Kristi, Oct 29): in the list's order, to the top,
 * the bottom, or the first of them at #n with the rest straight after. Everyone else keeps their order.
 */
export function blockTo(list: string[], block: string[], where: 'top' | 'bottom' | number): string[] {
  const moving = list.filter((id) => block.includes(id));
  const rest = list.filter((id) => !block.includes(id));
  const at = where === 'top' ? 0 : where === 'bottom' ? rest.length : Math.min(Math.max(where, 1), rest.length + 1) - 1;
  return [...rest.slice(0, at), ...moving, ...rest.slice(at)];
}

/** Move a book to #n in a full shelf list (the screen's copy while the database saves it). */
export function moveTo(list: string[], id: string, n: number): string[] {
  const rest = list.filter((x) => x !== id);
  const at = Math.min(Math.max(n, 1), rest.length + 1) - 1;
  rest.splice(at, 0, id);
  return rest;
}

// ---------- the columns ----------

/** "4 months" waiting, from the tab's date. */
export function waitingText(from: string, today: string): string {
  if (!from) return '';
  const d = Math.max(0, daysBetween(from, today));
  if (d < 14) return d === 1 ? '1 day' : `${d} days`;
  if (d < 61) return `${Math.floor(d / 7)} wk`;
  if (d < 730) return `${Math.floor(d / 30.4)} mo`;
  return `${Math.floor(d / 365.25)} yr`;
}

const FORMAT_NAMES: Record<string, string> = {
  kindle: 'Kindle', audible: 'Audible', paper: 'Paper', apple_books: 'Apple Books', google_play: 'Google Play', kobo: 'Kobo',
  libro_fm: 'Libro.fm', pdf_epub: 'PDF', article: 'Article', libby: 'Libby', hoopla: 'Hoopla',
};

/** The Copy column: the formats you have now, "Library" for a hold or loan, else null (the dashed Not owned yet). */
export function copyText(b: Pick<QueueRow, 'formats' | 'library' | 'owned'>): string | null {
  const names = [...new Set(b.formats.map((f) => FORMAT_NAMES[f] ?? f))];
  if (b.library.length && !b.owned) return b.library.includes('on_hold') && !b.library.includes('borrowed') ? 'Library hold' : 'Library';
  return names.length ? names.join(' · ') : b.library.length ? 'Library' : null;
}

/** The count on a tab or beside "Select all": "2,108". */
export const num = fmtNum;
/** A count in words: "1 book", "14 books" (R7: headings and count lines use words, not brackets). */
export const booksText = (n: number) => `${num(n)} ${n === 1 ? 'book' : 'books'}`;

/** What Select several's bar says: "3 selected", or "None selected" (one wording, R8). */
export const selectedText = (n: number) => (n ? `${num(n)} selected` : 'None selected');

/** The value every row shares (a bulk menu's ✓), or undefined when they differ or none are picked. */
export function shared<R, V>(rows: R[], of: (r: R) => V): V | undefined {
  if (!rows.length) return undefined;
  const first = of(rows[0]!);
  return rows.every((r) => of(r) === first) ? first : undefined;
}

/** Paging (board 6): 20 at a time, then Show 20 more · Show all. */
export const PAGE = 20;

/** The other list shelf, for "↑ Up Next" / "↓ Future reads". */
export const otherShelf = (t: ListTab): ListTab => (t === 'up_next' ? 'future_reads' : 'up_next');
export const shelfName = (t: ListTab | 'reference' | 'not_for_me') =>
  t === 'up_next' ? 'Up Next' : t === 'future_reads' ? 'Future reads' : t === 'reference' ? 'Reference' : 'Not for me';

/** The notice after a bulk change: "3 books moved to Up Next · 1 left as it was". */
export function bulkMessage(done: number, what: string, skipped: number): string {
  const books = done === 1 ? '1 book' : `${num(done)} books`;
  return `${books} ${what}${skipped ? ` · ${num(skipped)} left as ${skipped === 1 ? 'it was' : 'they were'}` : ''}`;
}
